import { faFile, faPenToSquare, faUserMinus } from '@fortawesome/pro-regular-svg-icons';
import { faCaretDown } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Box,
  ContextMenu,
  ContextMenuItem,
  ContextMenuList,
  ContextMenuTrigger,
  Divider,
  Flex,
  Text,
} from '@modusign/design-system';
import keyBy from 'lodash/keyBy';
import { useState } from 'react';
import OutsideClickHandler from 'react-outside-click-handler';

import useResponsiveValue from '@app/hooks/useResponsiveValue';

import { ResourceRole } from '../../types';
import ResourceRoleSubjectLabel from '../ResourceRoleSubjectLabel';

interface ResourceRoleListProps<T extends string> {
  resourceRoles: ResourceRole<T>[];
  hasEditPermission: boolean;
  resourceRoleTypes: { name: T; description: string }[];
  onChangeResourceRole: (value: ResourceRole<T>) => void;
  onRemoveResourceRole: (value: ResourceRole<T>) => void;
}

export default function ResourceRoleList<T extends string>({
  hasEditPermission,
  resourceRoleTypes,
  resourceRoles,
  onChangeResourceRole,
  onRemoveResourceRole,
}: ResourceRoleListProps<T>) {
  const isMobileWidth = useResponsiveValue([true, true, false]);
  const [isContextMenuOpen, setContextMenuOpen] = useState<ResourceRole<T> | null>(null);
  const resourceRoleMap = keyBy(resourceRoleTypes, 'name');

  const [hasChanged, setHasChanged] = useState(false);

  return (
    <Box as="table" width="100%">
      <Box as="thead" color="textNeutralLight" fontSize="sm">
        <tr>
          <Box as="th" px={4} py={3} textAlign="left">
            이름
          </Box>
          <Box as="th" px={4} py={3} width="90px">
            역할
          </Box>
        </tr>
      </Box>
      <Box
        as="tbody"
        borderBottom="1px solid"
        borderBottomColor="tableBorderNeutralLight"
        borderTop="1px solid"
        borderTopColor="tableBorderNeutralLight"
        flexDirection="column"
        maxHeight="350px"
        overflowY="auto"
      >
        {resourceRoles.length === 0 && (
          <Box as="tr" height="200px">
            <Box as="td" colSpan={2} textAlign="center">
              <Text>
                {hasChanged ? '변경한 공유 설정을 적용해 주세요.' : '공유된 멤버·그룹이 없습니다.'}
              </Text>
            </Box>
          </Box>
        )}
        {resourceRoles.map((rr) => (
          <Box
            as="tr"
            borderBottom="1px solid"
            borderBottomColor="tableBorderNeutralLight"
            height="52px"
            key={rr.subject.subjectType + rr.subject.subjectId}
            sx={{ '&:hover': { bg: 'tableBackgroundNeutralSlightlyDark' } }}
          >
            <Box as="td" px={4}>
              <ResourceRoleSubjectLabel role={rr.subject} />
            </Box>
            <Box as="td" px={4}>
              {hasEditPermission && !isMobileWidth ? (
                <OutsideClickHandler
                  disabled={isContextMenuOpen !== rr}
                  onOutsideClick={() => {
                    setContextMenuOpen(null);
                  }}
                >
                  <Box width="100%" />
                  <ContextMenu
                    isOpen={isContextMenuOpen === rr}
                    onOpenChange={() => {
                      setContextMenuOpen((prev) => (prev === rr ? null : rr));
                    }}
                  >
                    <ContextMenuTrigger>
                      <Flex
                        alignItems="center"
                        as="button"
                        borderRadius="3px"
                        color="textNeutralLight"
                        justifyContent="space-between"
                        sx={{
                          '&:hover': {
                            bg: 'buttonBackgroundNeutralBase',
                          },
                        }}
                        width="100%"
                      >
                        <Text fontSize="sm">{resourceRoleMap[rr.name].description}</Text>
                        <FontAwesomeIcon color="currentColor" icon={faCaretDown} />
                      </Flex>
                    </ContextMenuTrigger>
                    <ContextMenuList right="-110px" top="25px" width="140px">
                      {resourceRoleTypes.map((type) => (
                        <ContextMenuItem
                          key={type.name}
                          onClick={() => {
                            setContextMenuOpen(null);
                            setHasChanged(true);
                            onChangeResourceRole({ subject: rr.subject, name: type.name });
                          }}
                        >
                          <Box
                            as={FontAwesomeIcon}
                            color="iconNeutralSlightlyDark"
                            icon={type.description === '편집자' ? faPenToSquare : faFile}
                            mr={2}
                            width={20}
                          />
                          <Text color="textNeutralSlightlyLight">{type.description}</Text>
                        </ContextMenuItem>
                      ))}
                      {resourceRoleTypes.length > 1 && <Divider my={3} />}
                      <ContextMenuItem
                        onClick={() => {
                          setContextMenuOpen(null);
                          setHasChanged(true);
                          onRemoveResourceRole(rr);
                        }}
                      >
                        <Box
                          as={FontAwesomeIcon}
                          color="iconNeutralSlightlyDark"
                          icon={faUserMinus}
                          mr={2}
                          width={20}
                        />
                        <Text color="textNeutralSlightlyLight">공유 제거</Text>
                      </ContextMenuItem>
                    </ContextMenuList>
                  </ContextMenu>
                </OutsideClickHandler>
              ) : (
                <Text color="textNeutralLight" fontSize="sm">
                  {resourceRoleMap[rr.name].description}
                </Text>
              )}
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
}
